<template>
  <div class="app-container">

    <!-- Content Section -->
    <div class="content-section">
      <el-empty
        v-if="!isLoggedIn"
        description="Please login on the dedicated login page."
        :image-size="140"
      />

      <!-- Logged In Profile View with Tabs -->
      <el-card v-else class="profile-shell">
        <div class="profile-hero">
          <div class="hero-left">
            <el-avatar :size="88" src="https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png"></el-avatar>
            <div class="hero-main">
              <div class="hero-name">{{ currentUser.username }}</div>
              <div class="hero-tags">
                <el-tag :type="getRoleTagType(currentUser.role)" effect="dark" size="medium">
                  {{ currentUser.role.toUpperCase() }}
                </el-tag>
                <el-tag size="mini" type="success" effect="dark">Active</el-tag>
              </div>
              <div class="hero-meta">
                <span><i class="el-icon-message"></i> {{ currentUser.email || 'Not provided' }}</span>
                <span><i class="el-icon-user"></i> {{ currentUser.username }}</span>
              </div>
            </div>
          </div>
          <div class="hero-actions">
            <el-button type="warning" plain icon="el-icon-switch-button" @click="handleLogout">
              Logout
            </el-button>
          </div>
        </div>

        <el-tabs v-model="activeName" @tab-click="handleTabClick" class="profile-tabs">
          <!-- Overview Tab -->
          <el-tab-pane label="Overview" name="overview">
            <div class="tab-content">
              <div class="overview-grid">
                <div class="overview-card">
                  <div class="overview-icon">
                    <i class="el-icon-user"></i>
                  </div>
                  <div class="overview-body">
                    <div class="overview-label">Username</div>
                    <div class="overview-value">{{ currentUser.username }}</div>
                  </div>
                </div>

                <div class="overview-card">
                  <div class="overview-icon">
                    <i class="el-icon-message"></i>
                  </div>
                  <div class="overview-body">
                    <div class="overview-label">Email</div>
                    <div class="overview-value">{{ currentUser.email || '-' }}</div>
                  </div>
                </div>

                <div class="overview-card">
                  <div class="overview-icon">
                    <i class="el-icon-s-custom"></i>
                  </div>
                  <div class="overview-body">
                    <div class="overview-label">Role</div>
                    <div class="overview-value">
                      <el-tag :type="getRoleTagType(currentUser.role)" effect="dark" size="mini">
                        {{ currentUser.role.toUpperCase() }}
                      </el-tag>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </el-tab-pane>

          <!-- Settings Tab -->
          <el-tab-pane label="Settings" name="settings">
            <div class="tab-content">
              <div class="tab-header">
                <h3 class="tab-title"><i class="el-icon-setting"></i> Account Settings</h3>
              </div>
              
              <!-- Email Change Section -->
              <div class="settings-section">
                <h4>Change Email Address</h4>
                <p class="settings-desc">Update your email address. You'll need to verify your new email.</p>
                
                <el-form :model="emailChangeForm" label-width="140px" style="max-width: 500px;">
                  <el-form-item label="Current Email">
                    <el-input :value="currentUser.email" disabled />
                  </el-form-item>
                  <el-form-item label="New Email">
                    <el-input v-model="emailChangeForm.newEmail" placeholder="Enter new email address" />
                  </el-form-item>
                  <el-form-item label="Password">
                    <el-input
                      v-model="emailChangeForm.password"
                      type="password"
                      placeholder="Enter your password for verification"
                    />
                  </el-form-item>
                  <el-form-item>
                    <el-button
                      type="primary"
                      @click="handleChangeEmail"
                      :loading="emailChangeLoading"
                    >
                      {{ emailChangeLoading ? 'Requesting...' : 'Request Email Change' }}
                    </el-button>
                  </el-form-item>
                </el-form>
              </div>

              <!-- Password Change Section -->
              <div class="settings-section">
                <h4>Change Password</h4>
                <p class="settings-desc">Update your account password. You'll need to enter your current password for verification.</p>
                
                <el-form :model="passwordChangeForm" label-width="180px" style="max-width: 500px;">
                  <el-form-item label="Current Password">
                    <el-input
                      v-model="passwordChangeForm.oldPassword"
                      type="password"
                      placeholder="Enter your current password"
                      show-password
                    />
                  </el-form-item>
                  <el-form-item label="New Password">
                    <el-input
                      v-model="passwordChangeForm.newPassword"
                      type="password"
                      placeholder="Enter new password (min 6 characters)"
                      show-password
                    />
                  </el-form-item>
                  <el-form-item label="Confirm New Password">
                    <el-input
                      v-model="passwordChangeForm.confirmPassword"
                      type="password"
                      placeholder="Confirm your new password"
                      show-password
                    />
                  </el-form-item>
                  <el-form-item>
                    <el-button
                      type="primary"
                      @click="handleChangePassword"
                      :loading="passwordChangeLoading"
                    >
                      {{ passwordChangeLoading ? 'Changing...' : 'Change Password' }}
                    </el-button>
                  </el-form-item>
                </el-form>
              </div>
            </div>
          </el-tab-pane>

          <!-- Admin: User Management -->
          <el-tab-pane v-if="currentUser.role === 'admin'" label="User Management" name="userManagement">
            <div class="tab-content">
              <div class="tab-header">
                <h3 class="tab-title"><i class="el-icon-user-solid"></i> User Management</h3>
                <div class="tab-header-actions">
                  <el-input
                    v-model="userSearchQuery"
                    placeholder="Search users..."
                    size="mini"
                    clearable
                    prefix-icon="el-icon-search"
                    style="width: 200px; margin-right: 12px;"
                  />
                  <el-button size="mini" icon="el-icon-refresh" @click="fetchAllUsers" :loading="userMgmtLoading">
                    Refresh
                  </el-button>
                </div>
              </div>

              <el-table
                :data="filteredUserList"
                v-loading="userMgmtLoading"
                border
                style="width: 100%;"
                row-key="username"
                :default-sort="{ prop: 'date_joined', order: 'descending' }"
              >
                <el-table-column prop="username" label="Username" min-width="140" sortable />
                <el-table-column prop="email" label="Email" min-width="180">
                  <template slot-scope="scope">
                    <span>{{ scope.row.email || '-' }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="role" label="Role" width="120" sortable>
                  <template slot-scope="scope">
                    <el-tag :type="getRoleTagType(scope.row.role)" effect="dark" size="mini">
                      {{ (scope.row.role || '').toUpperCase() }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="Status" width="140">
                  <template slot-scope="scope">
                    <el-tag :type="scope.row.is_active ? 'success' : 'danger'" effect="plain" size="mini">
                      {{ scope.row.is_active ? 'Active' : 'Inactive' }}
                    </el-tag>
                    <el-tag 
                      :type="scope.row.email_verified ? 'success' : 'warning'" 
                      effect="plain" 
                      size="mini"
                      style="margin-left: 4px;"
                    >
                      {{ scope.row.email_verified ? 'Verified' : 'Unverified' }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="date_joined" label="Joined" width="110" sortable>
                  <template slot-scope="scope">
                    <span>{{ formatDate(scope.row.date_joined) }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="last_login" label="Last Login" width="110" sortable>
                  <template slot-scope="scope">
                    <span>{{ formatDate(scope.row.last_login) || 'Never' }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="Actions" width="180" fixed="right">
                  <template slot-scope="scope">
                    <el-button size="mini" icon="el-icon-edit" @click="openEditUser(scope.row)">Edit</el-button>
                    <el-dropdown trigger="click" @command="handleUserAction($event, scope.row)" style="margin-left: 8px;">
                      <el-button size="mini" icon="el-icon-more"></el-button>
                      <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item command="resetPassword" icon="el-icon-key">
                          Reset Password
                        </el-dropdown-item>
                        <el-dropdown-item 
                          :command="scope.row.is_active ? 'deactivate' : 'activate'"
                          :icon="scope.row.is_active ? 'el-icon-circle-close' : 'el-icon-circle-check'"
                          :disabled="scope.row.username === currentUser.username"
                        >
                          {{ scope.row.is_active ? 'Deactivate' : 'Activate' }}
                        </el-dropdown-item>
                        <el-dropdown-item 
                          command="delete" 
                          icon="el-icon-delete"
                          :disabled="scope.row.username === currentUser.username"
                          divided
                        >
                          <span style="color: #f56c6c;">Delete User</span>
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </template>
                </el-table-column>
              </el-table>

              <div class="user-stats">
                <span>Total: {{ userList.length }} users</span>
                <span v-if="userSearchQuery">| Showing: {{ filteredUserList.length }} results</span>
              </div>
            </div>

            <!-- User Details Dialog with Modular Editing -->
            <el-dialog
              title="User Details"
              :visible.sync="editUserDialogVisible"
              width="560px"
              class="user-details-dialog"
            >
              <div class="user-detail-rows">
                <!-- Username Row -->
                <div class="detail-row">
                  <div class="detail-label">Username</div>
                  <div class="detail-value" v-if="editingField !== 'username'">
                    <span class="value-text">{{ editUserForm.username }}</span>
                    <el-button 
                      v-if="editUserOriginalUsername !== currentUser.username"
                      type="text" 
                      icon="el-icon-edit" 
                      size="mini"
                      @click="startEditField('username')"
                    />
                    <span v-else class="form-hint">(Cannot edit your own)</span>
                  </div>
                  <div class="detail-edit" v-else>
                    <el-input 
                      v-model="editFieldValue" 
                      size="small" 
                      placeholder="New username"
                      @keyup.enter.native="saveFieldEdit('username')"
                    />
                    <el-button type="primary" size="mini" :loading="editUserSaving" @click="saveFieldEdit('username')">Save</el-button>
                    <el-button size="mini" @click="cancelFieldEdit">Cancel</el-button>
                  </div>
                </div>

                <!-- Email Row -->
                <div class="detail-row">
                  <div class="detail-label">Email</div>
                  <div class="detail-value" v-if="editingField !== 'email'">
                    <span class="value-text">{{ editUserForm.email || '-' }}</span>
                    <el-button type="text" icon="el-icon-edit" size="mini" @click="startEditField('email')" />
                  </div>
                  <div class="detail-edit" v-else>
                    <el-input 
                      v-model="editFieldValue" 
                      size="small" 
                      placeholder="Email address"
                      @keyup.enter.native="saveFieldEdit('email')"
                    />
                    <el-button type="primary" size="mini" :loading="editUserSaving" @click="saveFieldEdit('email')">Save</el-button>
                    <el-button size="mini" @click="cancelFieldEdit">Cancel</el-button>
                  </div>
                </div>

                <!-- Role Row -->
                <div class="detail-row">
                  <div class="detail-label">Role</div>
                  <div class="detail-value" v-if="editingField !== 'role'">
                    <el-tag :type="getRoleTagType(editUserForm.role)" effect="dark" size="small">
                      {{ (editUserForm.role || '').toUpperCase() }}
                    </el-tag>
                    <el-button type="text" icon="el-icon-edit" size="mini" @click="startEditField('role')" />
                  </div>
                  <div class="detail-edit" v-else>
                    <el-select v-model="editFieldValue" size="small" placeholder="Select role">
                      <el-option label="Admin" value="admin" />
                      <el-option label="Enterprise" value="enterprise" />
                      <el-option label="Community" value="community" />
                    </el-select>
                    <el-button type="primary" size="mini" :loading="editUserSaving" @click="saveFieldEdit('role')">Save</el-button>
                    <el-button size="mini" @click="cancelFieldEdit">Cancel</el-button>
                  </div>
                </div>

                <!-- Account Status Row -->
                <div class="detail-row">
                  <div class="detail-label">Account Status</div>
                  <div class="detail-value">
                    <el-switch
                      v-model="editUserForm.is_active"
                      active-text="Active"
                      inactive-text="Inactive"
                      active-color="#67c23a"
                      inactive-color="#f56c6c"
                      :disabled="editUserOriginalUsername === currentUser.username"
                      @change="saveDirectToggle('is_active', $event)"
                    />
                    <span v-if="editUserOriginalUsername === currentUser.username" class="form-hint">(Cannot change)</span>
                  </div>
                </div>

                <!-- Email Verified Row -->
                <div class="detail-row">
                  <div class="detail-label">Email Verified</div>
                  <div class="detail-value">
                    <el-switch
                      v-model="editUserForm.email_verified"
                      active-text="Verified"
                      inactive-text="Unverified"
                      active-color="#67c23a"
                      inactive-color="#e6a23c"
                      @change="saveDirectToggle('email_verified', $event)"
                    />
                  </div>
                </div>

                <!-- Info Rows (Read-only) -->
                <div class="detail-row info-row">
                  <div class="detail-label">Joined</div>
                  <div class="detail-value">
                    <span class="value-text muted">{{ formatDateTime(editUserForm.date_joined) || 'Unknown' }}</span>
                  </div>
                </div>

                <div class="detail-row info-row">
                  <div class="detail-label">Last Login</div>
                  <div class="detail-value">
                    <span class="value-text muted">{{ formatDateTime(editUserForm.last_login) || 'Never' }}</span>
                  </div>
                </div>
              </div>

              <span slot="footer" class="dialog-footer">
                <el-button @click="editUserDialogVisible = false">Close</el-button>
              </span>
            </el-dialog>
          </el-tab-pane>
        </el-tabs>
      </el-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ProfileView',
  props: {
    isLoggedIn: {
      type: Boolean,
      default: false
    },
    currentUser: {
      type: Object,
      default: () => ({ username: '', email: '', role: 'community' })
    }
  },
  data() {
    return {
      activeName: 'overview',
      userMgmtLoading: false,
      userList: [],
      userSearchQuery: '',
      editUserDialogVisible: false,
      editUserSaving: false,
      editUserOriginalUsername: '',
      editingField: null,
      editFieldValue: '',
      editUserForm: {
        username: '',
        email: '',
        role: 'community',
        is_active: true,
        email_verified: false,
        date_joined: null,
        last_login: null
      },
      emailChangeForm: {
        newEmail: '',
        password: ''
      },
      emailChangeLoading: false,
      passwordChangeForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordChangeLoading: false
    }
  },
  computed: {
    filteredUserList() {
      if (!this.userSearchQuery) return this.userList
      const q = this.userSearchQuery.toLowerCase()
      return this.userList.filter(u => 
        (u.username && u.username.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.role && u.role.toLowerCase().includes(q))
      )
    }
  },
  methods: {
    handleTabClick(tab) {
      if (tab && tab.name === 'userManagement') {
        this.fetchAllUsers()
      }
    },
    async handleLogout() {
      try {
        await axios.post('/api/user/logout')
        this.$message.success('Logged out successfully')
        // Reset URL to home and reload
        window.location.href = '/'
      } catch (error) {
        console.error('Logout error:', error)
        this.$message.error('Failed to logout')
      }
    },
    getRoleTagType(role) {
      const typeMap = {
        'admin': 'danger',
        'enterprise': 'warning',
        'community': 'info'
      }
      return typeMap[role] || 'info'
    },

    async fetchAllUsers() {
      if (this.currentUser.role !== 'admin') return
      this.userMgmtLoading = true
      try {
        const res = await axios.get('/api/user/all-users')
        if (res && res.data && res.data.code === 200) {
          const users = (res.data.data && res.data.data.users) ? res.data.data.users : []
          // Normalize for table with all new fields
          this.userList = users.map(u => ({
            username: u.username,
            email: u.email || '',
            role: u.role || 'community',
            is_active: u.is_active !== false,
            email_verified: u.email_verified || false,
            date_joined: u.date_joined || null,
            last_login: u.last_login || null
          }))
        } else {
          this.$message.error(res.data?.error || 'Failed to load users')
        }
      } catch (e) {
        this.$message.error('Failed to load users')
      } finally {
        this.userMgmtLoading = false
      }
    },

    openEditUser(row) {
      // Store original username separately to prevent reactivity issues
      this.editUserOriginalUsername = row.username
      this.editingField = null
      this.editFieldValue = ''
      this.editUserForm = {
        username: row.username,
        email: row.email || '',
        role: row.role || 'community',
        is_active: row.is_active !== false,
        email_verified: row.email_verified || false,
        date_joined: row.date_joined || null,
        last_login: row.last_login || null
      }
      this.editUserDialogVisible = true
    },

    startEditField(field) {
      this.editingField = field
      this.editFieldValue = this.editUserForm[field] || ''
    },

    cancelFieldEdit() {
      this.editingField = null
      this.editFieldValue = ''
    },

    async saveFieldEdit(field) {
      if (!this.editUserOriginalUsername) {
        this.$message.error('No user selected')
        return
      }

      const newValue = typeof this.editFieldValue === 'string' ? this.editFieldValue.trim() : this.editFieldValue
      const oldValue = this.editUserForm[field]

      // Skip if value hasn't changed
      if (newValue === oldValue) {
        this.cancelFieldEdit()
        return
      }

      // Validate based on field type
      if (field === 'username') {
        if (!newValue) {
          this.$message.error('Username cannot be empty')
          return
        }
        if (newValue.length < 3) {
          this.$message.error('Username must be at least 3 characters')
          return
        }
        if (newValue.length > 30) {
          this.$message.error('Username must be at most 30 characters')
          return
        }
        if (!/^[a-zA-Z0-9_-]+$/.test(newValue)) {
          this.$message.error('Username can only contain letters, numbers, underscores, and hyphens')
          return
        }
      }

      this.editUserSaving = true
      try {
        // Build payload with only the changed field
        const payload = { username: this.editUserOriginalUsername }
        
        if (field === 'username') {
          payload.new_username = newValue
        } else {
          payload[field] = newValue
        }

        const res = await axios.post('/api/user/admin-update-user', payload)
        if (res && res.data && res.data.code === 200) {
          // Update local form data
          if (field === 'username') {
            this.editUserForm.username = newValue
            this.editUserOriginalUsername = newValue // Update original since it changed
            this.$message.success(`Username changed to "${newValue}"`)
          } else {
            this.editUserForm[field] = newValue
            this.$message.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated`)
          }
          this.cancelFieldEdit()
          await this.fetchAllUsers()
        } else {
          this.$message.error(res.data?.error || 'Failed to update')
        }
      } catch (e) {
        this.$message.error(e.response?.data?.error || 'Failed to update')
      } finally {
        this.editUserSaving = false
      }
    },

    async saveDirectToggle(field, value) {
      if (!this.editUserOriginalUsername) return

      try {
        const payload = { 
          username: this.editUserOriginalUsername,
          [field]: value
        }

        const res = await axios.post('/api/user/admin-update-user', payload)
        if (res && res.data && res.data.code === 200) {
          const fieldName = field === 'is_active' ? 'Account status' : 'Email verified'
          this.$message.success(`${fieldName} updated`)
          await this.fetchAllUsers()
        } else {
          // Revert the toggle on failure
          this.editUserForm[field] = !value
          this.$message.error(res.data?.error || 'Failed to update')
        }
      } catch (e) {
        // Revert the toggle on failure
        this.editUserForm[field] = !value
        this.$message.error(e.response?.data?.error || 'Failed to update')
      }
    },

    handleUserAction(command, row) {
      switch (command) {
        case 'resetPassword':
          this.confirmResetPassword(row)
          break
        case 'activate':
          this.toggleUserActive(row, true)
          break
        case 'deactivate':
          this.toggleUserActive(row, false)
          break
        case 'delete':
          this.confirmDeleteUser(row)
          break
      }
    },

    confirmResetPassword(row) {
      if (!row || !row.username) return
      this.$confirm(
        `Send a password reset email to "${row.username}" (${row.email || 'no email'})?`,
        'Reset Password',
        {
          confirmButtonText: 'Send Reset Email',
          cancelButtonText: 'Cancel',
          type: 'info'
        }
      ).then(() => this.resetUserPassword(row.username))
        .catch(() => {})
    },

    async resetUserPassword(username) {
      try {
        const res = await axios.post('/api/user/admin-reset-password', { username })
        if (res && res.data && res.data.code === 200) {
          this.$message.success(res.data.data?.message || 'Password reset email sent')
        } else {
          this.$message.error(res.data?.error || 'Failed to send reset email')
        }
      } catch (e) {
        this.$message.error(e.response?.data?.error || 'Failed to send reset email')
      }
    },

    async toggleUserActive(row, active) {
      const action = active ? 'activate' : 'deactivate'
      try {
        const res = await axios.post('/api/user/admin-update-user', {
          username: row.username,
          is_active: active
        })
        if (res && res.data && res.data.code === 200) {
          this.$message.success(`User ${action}d successfully`)
          await this.fetchAllUsers()
        } else {
          this.$message.error(res.data?.error || `Failed to ${action} user`)
        }
      } catch (e) {
        this.$message.error(`Failed to ${action} user`)
      }
    },

    confirmDeleteUser(row) {
      if (!row || !row.username) return
      this.$confirm(
        `Delete user "${row.username}"? This action cannot be undone and will remove all user data.`,
        'Delete User',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning',
          confirmButtonClass: 'el-button--danger'
        }
      ).then(() => this.deleteUser(row.username))
        .catch(() => {})
    },

    async deleteUser(username) {
      try {
        const res = await axios.post('/api/user/admin-delete-user', { username })
        if (res && res.data && res.data.code === 200) {
          this.$message.success('User deleted successfully')
          await this.fetchAllUsers()
        } else {
          this.$message.error(res.data?.error || 'Failed to delete user')
        }
      } catch (e) {
        this.$message.error('Failed to delete user')
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return null
      try {
        const date = new Date(dateStr)
        return date.toLocaleDateString()
      } catch (e) {
        return dateStr
      }
    },

    formatDateTime(dateStr) {
      if (!dateStr) return null
      try {
        const date = new Date(dateStr)
        return date.toLocaleString()
      } catch (e) {
        return dateStr
      }
    },

    async handleChangeEmail() {
      if (!this.emailChangeForm.newEmail) {
        this.$message.warning('Please enter a new email address')
        return
      }

      if (!this.emailChangeForm.password) {
        this.$message.warning('Please enter your password')
        return
      }

      if (this.emailChangeForm.newEmail === this.currentUser.email) {
        this.$message.warning('New email is the same as current email')
        return
      }

      this.emailChangeLoading = true

      try {
        const res = await axios.post('/api/user/request-email-change', {
          new_email: this.emailChangeForm.newEmail,
          password: this.emailChangeForm.password
        })

        if (res && res.data && res.data.code === 200) {
          this.$message.success('Verification email sent to your new address. Please check your inbox.')
          this.emailChangeForm = { newEmail: '', password: '' }
        } else if (res && res.data && res.data.code === 429) {
          this.$message.warning('Please wait before requesting another email change')
        } else {
          this.$message.error(res.data?.error || 'Failed to request email change')
        }
      } catch (error) {
        console.error('Email change error:', error)
        if (error.response && error.response.data && error.response.data.code === 429) {
          this.$message.warning('Please wait before requesting another email change')
        } else {
          this.$message.error(error.response?.data?.error || 'Failed to request email change')
        }
      } finally {
        this.emailChangeLoading = false
      }
    },

    async handleChangePassword() {
      if (!this.passwordChangeForm.oldPassword) {
        this.$message.warning('Please enter your current password')
        return
      }

      if (!this.passwordChangeForm.newPassword) {
        this.$message.warning('Please enter a new password')
        return
      }

      if (this.passwordChangeForm.newPassword.length < 6) {
        this.$message.warning('Password must be at least 6 characters')
        return
      }

      if (!this.passwordChangeForm.confirmPassword) {
        this.$message.warning('Please confirm your new password')
        return
      }

      if (this.passwordChangeForm.newPassword !== this.passwordChangeForm.confirmPassword) {
        this.$message.warning('New passwords do not match')
        return
      }

      if (this.passwordChangeForm.oldPassword === this.passwordChangeForm.newPassword) {
        this.$message.warning('New password must be different from current password')
        return
      }

      this.passwordChangeLoading = true

      try {
        const res = await axios.post('/api/user/change-password', {
          old_password: this.passwordChangeForm.oldPassword,
          new_password: this.passwordChangeForm.newPassword,
          confirm_password: this.passwordChangeForm.confirmPassword
        })

        if (res && res.data && res.data.code === 200) {
          this.$message.success('Password changed successfully')
          this.passwordChangeForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
        } else {
          this.$message.error(res.data?.error || 'Failed to change password')
        }
      } catch (error) {
        console.error('Password change error:', error)
        this.$message.error(error.response?.data?.error || 'Failed to change password')
      } finally {
        this.passwordChangeLoading = false
      }
    }
  }
}
</script>

<style scoped>
.app-container {
  min-height: 100vh;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
}

.navigate-bar {
  background: #ffffff;
  padding: 24px 32px;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
}

.content-section {
  padding: 32px;
  flex: 1;
  display: flex;
}

/* Profile Shell */
.profile-shell {
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.profile-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(17, 94, 163, 0.08));
  border-radius: 12px;
  margin-bottom: 16px;
}

.hero-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hero-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hero-name {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.hero-tags {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.hero-meta {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  color: #475569;
  font-size: 13px;
}

.hero-meta span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.hero-actions .el-button {
  border-color: #f59e0b;
}

.profile-tabs .el-tabs__header {
  margin: 0 0 12px 0;
}

.profile-tabs {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.profile-tabs :deep(.el-tabs__content) {
  flex: 1;
  min-height: 0;
}

.tab-content {
  background: #ffffff;
  padding: 16px;
  height: 100%;
  min-height: 0;
  overflow: auto;
}

.tab-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 12px;
}

/* Settings Section */
.settings-section {
  background: #f8fafc;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
}

.settings-section h4 {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.settings-desc {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 20px;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}

.overview-card {
  display: flex;
  gap: 12px;
  align-items: center;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
}

.overview-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
  flex: 0 0 40px;
}

.overview-icon i {
  font-size: 18px;
}

.overview-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.overview-label {
  font-size: 12px;
  color: #64748b;
}

.overview-value {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tab-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.tab-header-actions {
  display: flex;
  align-items: center;
}

.user-stats {
  margin-top: 12px;
  font-size: 13px;
  color: #64748b;
}

.form-hint {
  font-size: 12px;
  color: #909399;
  margin-left: 8px;
}

.form-warning {
  display: block;
  font-size: 12px;
  color: #e6a23c;
  margin-top: 4px;
}

.form-warning i {
  margin-right: 4px;
}

.form-text {
  color: #606266;
  font-size: 14px;
}

/* User Details Dialog Styles */
.user-detail-rows {
  padding: 0 8px;
}

.detail-row {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row.info-row {
  background: #fafafa;
  margin: 0 -8px;
  padding: 12px 16px;
}

.detail-label {
  width: 120px;
  font-weight: 500;
  color: #606266;
  flex-shrink: 0;
}

.detail-value {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-value .value-text {
  font-size: 14px;
  color: #303133;
}

.detail-value .value-text.muted {
  color: #909399;
}

.detail-edit {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-edit .el-input,
.detail-edit .el-select {
  flex: 1;
  max-width: 200px;
}

.user-details-dialog .el-dialog__body {
  padding: 10px 20px 20px;
}

/* Responsive */
@media (max-width: 768px) {
  .profile-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-actions {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  .tab-content {
    padding: 12px;
  }

  .overview-grid {
    grid-template-columns: 1fr;
  }
}
</style>
